/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */

import Vue from "vue"
import api from "@/config/api"
import { defaultUserProperties } from "@/config/userPropertyConstants"
import { LOAD_STATES, PagedRequestHandler } from "../utils/PagedRequestHandler"

const userRequestHandler = Vue.observable(new PagedRequestHandler(api.USERS))

const requestFilter = function (sortOption, rootGetters) {
  let finalObject = {}

  finalObject["sort"] = sortOption.urlValue + (sortOption.asc ? ",asc" : ",desc")

  if (rootGetters["tenants/hasTenantFilter"]) {
    finalObject["tenantId"] = rootGetters["tenants/getTenantFilterId"]
  }

  return finalObject
}

// Set of currently supported roles
const supportedRoles = ["meetingAdministration", "staffUserAdministration", "cockpit", "Customer"]

const supportedRolesAdmin = [
  "speakerInfos",
  "meetingAdministration",
  "Customer",
  "lockScreenImages",
  "staffUserAdministration",
  "travelInfos",
  "manual",
  "agenda",
  "cockpit",
  "tenantAdministration",
]

// Set of roles that each user must have to guarantee expected system behavior
const mandatoryRoles = ["Customer"]

const filterRoles = function (roles, isSuperAdmin) {
  if (!roles) {
    return []
  }

  let cleanRoles = []
  for (const idx in roles) {
    let roleName = roles[idx].name

    if (
      (isSuperAdmin && supportedRolesAdmin.includes(roleName)) ||
      (!isSuperAdmin && supportedRoles.includes(roleName))
    ) {
      cleanRoles.push(roles[idx])
    }
  }

  return cleanRoles
}

const defaultRoleName = "Customer"

export default {
  namespaced: true,

  state: {
    users: {},
    roles: {},
    publicUserSet: {},
    searchQuery: "",
    status: {
      roles: LOAD_STATES.UNLOADED,
      users: LOAD_STATES.UNLOADED,
      tenants: LOAD_STATES.UNLOADED,
      publicUserSet: LOAD_STATES.UNLOADED,
    },
    loadingPublicUserIds: [],
    sortOptions: {
      active: 0,
      options: [
        {
          text: "ascendingUserName",
          urlValue: "username",
          asc: true,
        },
        {
          text: "descendingUserName",
          urlValue: "username",
          asc: false,
        },
        {
          text: "ascendingFirstName",
          urlValue: "firstname",
          asc: true,
        },
        {
          text: "descendingFirstName",
          urlValue: "firstname",
          asc: false,
        },
        {
          text: "ascendingLastName",
          urlValue: "lastname",
          asc: true,
        },
        {
          text: "descendingLastName",
          urlValue: "lastname",
          asc: false,
        },
        {
          text: "ascendingMail",
          urlValue: "email",
          asc: true,
        },
        {
          text: "descendingMail",
          urlValue: "email",
          asc: false,
        },
        {
          text: "ascendingModificationDate",
          urlValue: "lastModifiedDate",
          asc: true,
        },
        {
          text: "descendingModificationDate",
          urlValue: "lastModifiedDate",
          asc: false,
        },
        {
          text: "ascendingCreationDate",
          urlValue: "createdDate",
          asc: true,
        },
        {
          text: "descendingCreationDate",
          urlValue: "createdDate",
          asc: false,
        },
      ],
    },
  },

  mutations: {
    /**
     * commit load initiation to state
     */
    loadStart(state, prop) {
      state.status[prop] = LOAD_STATES.LOADING
    },

    /**
     * commit load error to state
     */
    loadError(state, prop) {
      state.status[prop] = LOAD_STATES.ERROR
    },

    /**
     * commit users to state
     *
     * @param state
     * @param users
     * @param purge
     */
    loadUsers(state, { users, purge }) {
      if (purge) {
        state.users = {}
      }

      // store user objects
      users.forEach((u) => {
        Vue.set(state.users, u.userId, {
          id: u.userId, // userId should never be null
          tenantId: u.tenantId,
          properties: Object.values(defaultUserProperties).reduce((result, key) => {
            if (u.properties[key]) {
              // Merge in retrieved users property data
              result[key] = u.properties[key]
            } else {
              // Ensure base property set is defined when it does not exist
              result[key] = {
                type: "STRING",
                value: "",
              }
            }

            return result
          }, {}),
        })
      })

      state.status.users = LOAD_STATES.LOADED
    },

    /**
     * commit roles to state
     *
     * @param state
     * @param roles
     */
    loadRoles(state, roles) {
      // store role objects
      roles.forEach((r) => {
        Vue.set(state.roles, r.id, r)
      })

      state.status.roles = LOAD_STATES.LOADED
    },

    addLoadingPublicUsers(state, loadingUserIds) {
      loadingUserIds.forEach((userId) => state.loadingPublicUserIds.push(userId))
    },

    /**
     * commit users to state
     *
     * @param state
     * @param users
     */
    loadPublicUserSet(state, users) {
      // store user objects
      users.forEach((u) => {
        Vue.set(state.publicUserSet, u.userId, u)
        // Remove user from list of loading ones
        const index = state.loadingPublicUserIds.findIndex((userId) => userId === u.userId)
        if (index >= 0) {
          state.loadingPublicUserIds.splice(index, 1)
        }
      })

      if (state.loadingPublicUserIds.length === 0) {
        state.status.publicUserSet = LOAD_STATES.LOADED
      }
    },

    createUser(state, user) {
      log.debug("Creating user:", user)
      Vue.set(state.users, user.userId, {
        id: user.userId, // userId will never be null
        tenantId: user.tenantId,
        properties: Object.values(defaultUserProperties).reduce((result, key) => {
          if (user.properties[key]) {
            // Merge in retrieved users property data
            result[key] = user.properties[key]
          } else {
            // Ensure base property set is defined when it does not exist
            result[key] = {
              type: "STRING",
              value: "",
            }
          }

          return result
        }, {}),
      })
    },

    updateUser(state, { userId, properties }) {
      log.debug("Updating user: ", userId, " : ", properties)

      for (const key of Object.keys(properties)) {
        // Use Vue set on the off chance a non-primitive is being assigned
        Vue.set(state.users[userId].properties, key, properties[key])
      }

      // update publicUserSet if applicable
      if (state.publicUserSet.hasOwnProperty(userId)) {
        Vue.set(state.publicUserSet[userId], "username", properties?.username?.value)
      }
    },

    updateUserRoles(state, { userId, roles }) {
      log.debug("Updating roles of user: ", userId, roles)
      Vue.set(state.users[userId], "roles", roles)
    },

    /**
     * commit user deletion to state
     *
     * @param state
     * @param id
     */
    deleteUser(state, { id }) {
      log.debug("deleting user:", id)
      Vue.delete(state.users, id)
      Vue.delete(state.publicUserSet, id)
    },

    /**
     * commit new search query value
     *
     * @param state
     * @param search
     */
    setSearchQuery(state, search) {
      state.searchQuery = search
    },

    /**
     * Set last active sort
     *
     * @param {*} state
     * @param {*} sortVal
     */
    setLastActiveSort(state, sortVal) {
      state.sortOptions.active = sortVal
    },

    /**
     * Reset search and sorting and set users load state back to UNLOADED
     * @param {*} state
     */
    resetUsersState(state) {
      state.searchQuery = ""
      state.sortOptions.active = 0
      userRequestHandler.setCurrentPage(0)
    },
  },

  actions: {
    /**
     * load new users from server
     */
    loadUsers({ commit, getters, dispatch }, { purge }) {
      if (userRequestHandler.LoadStatus === LOAD_STATES.LOADING) {
        return
      }

      if (purge) {
        userRequestHandler.setCurrentPage(0)
      }

      commit("loadStart", "users")
      userRequestHandler
        .retrieveNextPage()
        .then((result) => {
          commit("loadUsers", {
            users: result,
            purge: purge,
          })

          dispatch("tenants/loadAssignedTenants", null, { root: true }) // for displaying tenant names

          let userSet = getters["list"]
          for (const user of userSet) {
            Vue.http.get(api.USER_ROLES(user.id)).then(
              (response) => {
                commit("updateUserRoles", {
                  userId: user.id,
                  roles: response.body,
                })
              },
              () => {
                commit("loadError", "users")
              }
            )
          }
        })
        .catch((error) => log.warn(error))
    },

    /**
     * Loads all available roles from the server and filters it
     * depending on the role of the current user
     */
    loadRoles({ commit, state, rootGetters }) {
      if (state.status.roles === LOAD_STATES.LOADING) {
        return
      }

      commit("loadStart", "roles")
      let currentUser = rootGetters["user/userSettings"]
      let isSuperAdmin = currentUser.tenantId === null

      Vue.http.get(api.ROLES()).then(
        (response) => {
          commit("loadRoles", filterRoles(response.body, isSuperAdmin))
        },
        () => {
          commit("loadError", "roles")
        }
      )
    },

    /**
     * Loads public usernames with their corresponding userId
     * based on the currently loaded meetings from the server
     */
    loadPublicUserSet({ commit, state, rootGetters }) {
      const meetingOwners = rootGetters["meetings/allMeetings"].map((meeting) => meeting.ownerId)
      const meetingCollaborators = rootGetters["meetings/collaboratorsList"]

      const uniqueUsers = [...new Set([...meetingOwners, ...meetingCollaborators])]
      const usersToLoad = uniqueUsers.filter((userId) => !state.loadingPublicUserIds.includes(userId))
      if (usersToLoad.length === 0) {
        return
      }
      commit("addLoadingPublicUsers", usersToLoad)
      commit("loadStart", "publicUserSet")

      Vue.http.get(api.PUBLIC_USERS_ID(usersToLoad)).then(
        (response) => {
          commit("loadPublicUserSet", response.body.content)
        },
        () => {
          commit("loadError", "publicUserSet")
        }
      )
    },

    /**
     * Completely replaces the role set of an existing user with the provided set.
     * This includes determining if roles need to be removed(if any) or added to
     * an existing users and processing server requests.
     */
    updateUserRoles({ commit, state }, { userId, roles }) {
      let addIdx = 0
      let rolesToAdd = []
      let remIdx = 0
      let rolesToRemove = []
      let finalRoles = []

      // temporary fix to ensure mandatory roles are set and the UI does not crash for underprivileged users
      // ToDo: remove as soon as the MCC performs proper permission checks everywhere
      mandatoryRoles.forEach((name) => {
        if (!roles.find((assignedRole) => assignedRole.hasOwnProperty("name") && assignedRole.name === name)) {
          let availableRole = Object.values(state.roles).find(
            (availableRole) => availableRole.hasOwnProperty("name") && availableRole.name === name
          )
          if (availableRole) {
            log.info("User is missing mandatory role ", name, " - Adding this role.")
            roles.push(availableRole)
          } else {
            log.error("User is missing mandatory role ", name, ", but the role is not available. - Can't assign it.")
          }
        }
      })

      if (state.users[userId].roles) {
        // Updating existing user; parse roles to add/remove
        for (let i = 0; i < roles.length || i < state.users[userId].roles.length; ++i) {
          if (i < roles.length) {
            if (state.users[userId].roles.filter((r) => r.id == roles[i].id).length == 0) {
              rolesToAdd.push(roles[i])
            } else {
              // This role is neither an add or delete, simply persist
              finalRoles.push(roles[i])
            }
          }
          if (
            i < state.users[userId].roles.length &&
            roles.filter((r) => r.id == state.users[userId].roles[i].id).length == 0
          ) {
            if (!(state.users[userId].roles[i].id in state.roles)) {
              // If the role is flagged to be removed however isn't a supported role,
              // ignore this change by simply re-adding it to the users role list.
              finalRoles.push(state.users[userId].roles[i])
            }
            rolesToRemove.push(state.users[userId].roles[i])
          }
        }
      } else {
        // New user add all roles
        rolesToAdd = roles
      }

      let callback = () => {
        if (addIdx < rolesToAdd.length) {
          let targetRole = rolesToAdd[addIdx++]
          // Simply guard to ensure a role update is not processed if its target role is not in store
          if (!(targetRole.id in state.roles)) {
            return callback()
          }
          targetRole = state.roles[targetRole.id] // Get the true role from store
          log.debug("Attaching user to role: " + targetRole.name + " : " + targetRole.id)
          return new Promise((res, rej) => {
            Vue.http.put(api.USER_ROLES(userId, targetRole.id)).then(
              () => {
                finalRoles.push(targetRole)
                res(callback())
              },
              (response) => {
                rej(response)
              }
            )
          })
        } else if (remIdx < rolesToRemove.length) {
          let targetRole = rolesToRemove[remIdx++]
          // Simply guard to ensure a role update is not processed if its target role is not in store
          if (!(targetRole.id in state.roles)) {
            return callback()
          }
          targetRole = state.roles[targetRole.id] // Get the true role from store
          log.debug("Detaching user from role: " + targetRole.name + " : " + targetRole.id)
          return new Promise((res, rej) => {
            Vue.http.delete(api.USER_ROLES(userId, targetRole.id)).then(
              () => {
                res(callback())
              },
              (response) => {
                rej(response)
              }
            )
          })
        } else {
          commit("updateUserRoles", { userId, roles: finalRoles })
          return finalRoles
        }
      }

      return callback()
    },

    createUser({ commit, dispatch }, { user, sendEmail, tenantId }) {
      let params = `?sendEmail=${sendEmail}`
      if (tenantId !== null) {
        params += `&tenantId=${tenantId}`
      }

      return new Promise((resolve, reject) => {
        Vue.http
          .post(`${api.USERS()}${params}`, {
            userId: "", // Empty the server will make us one
            properties: user.properties,
          })
          .then(
            (response) => {
              log.debug("User created on server, storing: " + response.data.userId)
              commit("createUser", response.data)

              dispatch("tenants/loadAssignedTenants", null, { root: true })
              dispatch("updateUserRoles", { userId: response.data.userId, roles: user.roles }).then((roles) => {
                log.debug("All roles attached to user. Role list for user: " + JSON.stringify(roles))

                resolve(response.data)
              }, reject)
            },
            (response) => {
              reject(response)
            }
          )
      })
    },

    updateUser({ commit, dispatch }, { user }) {
      let body = {}
      // Either copy over the property provided or provide an initial value for a complete object
      for (const key of Object.values(defaultUserProperties)) {
        body[key] = user.properties[key] || ""
      }
      return new Promise((resolve, reject) => {
        Vue.http.put(api.USER_PROPERTIES(user.id), body).then(
          (response) => {
            commit("updateUser", { userId: user.id, properties: response.data })

            dispatch("updateUserRoles", { userId: user.id, roles: user.roles }).then((roles) => {
              log.debug("All roles updated for user. New role list for user: " + JSON.stringify(roles))
              resolve(response.data)
            }, reject)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    updatePassword({ dispatch }, { userId, password, sendMail }) {
      log.info("Going to update password in backend for user: " + userId)

      if (sendMail) {
        return dispatch("sendPasswordLink", { userId })
      }

      return new Promise((resolve, reject) => {
        Vue.http
          .put(api.USER_PASSWORD(userId), {
            newPassword: password,
          })
          .then(
            (response) => {
              resolve(response.data)
            },
            (response) => {
              reject(response)
            }
          )
      })
    },

    // eslint-disable-next-line no-empty-pattern
    sendPasswordLink({}, { userId }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.SEND_RESET_TOKEN_LINK(userId)).then(
          (response) => {
            log.debug("Reset email sent to user: ", userId)
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    // eslint-disable-next-line no-empty-pattern
    signOutUser({}, { userId }) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.USER_SESSIONS(userId)).then(
          () => {
            log.debug("Signed out user ", userId, " from all devices.")
            resolve()
          },
          (response) => {
            log.error("Error when trying to sign out user ", userId, " from all devices. ", response)
            reject(response)
          }
        )
      })
    },

    deleteUser({ commit }, { id }) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.USERS(id)).then(
          (response) => {
            commit("deleteUser", { id })
            resolve(response) // delete endpoint does not provide response payload
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    /**
     * Update filters and search string in user request handler
     * and load fresh page afterwards
     *
     * @param state
     * @param dispatch
     * @param rootGetters
     */
    updateHandlerAndLoadUsers({ state, dispatch, rootGetters }) {
      userRequestHandler.setFilter(requestFilter(state.sortOptions.options[state.sortOptions.active], rootGetters))
      userRequestHandler.setSearchString(state.searchQuery)
      dispatch("loadUsers", { purge: true })
    },

    /**
     * Update last active sort and trigger handler update + user reload
     *
     * @param commit
     * @param dispatch
     * @param sortVal
     */
    setLastActiveSort({ commit, dispatch }, sortVal) {
      commit("setLastActiveSort", sortVal)
      dispatch("updateHandlerAndLoadUsers")
    },

    /**
     * Update search query and trigger handler update + user reload
     *
     * @param commit
     * @param dispatch
     * @param search
     */
    setSearchQuery({ commit, dispatch }, search) {
      commit("setSearchQuery", search)
      dispatch("updateHandlerAndLoadUsers")
    },
  },

  getters: {
    /**
     * Get current status of indicated store object
     *
     * @returns {String}
     */
    getStatus: (state) => (prop) => state.status[prop],

    /**
     * Get whether users have successfully loaded
     *
     * @returns {boolean}
     */
    hasLoaded: (state) => (prop) => state.status[prop] === LOAD_STATES.LOADED,

    /**
     * Return current user load state on store.
     *
     * @returns String
     */
    getUserLoadState: () => userRequestHandler.LoadStatus,

    /**
     * Get array of users
     *
     * @returns {any[]}
     */
    list: (state) => Object.values(state.users),

    /**
     * Get public user set for meeting transfers
     *
     * @returns {any[]}
     */
    publicUserSet: (state) => Object.values(state.publicUserSet),

    /**
     * Indicates if a given username has already been associated with another loaded user.
     *
     * @returns {Boolean}
     */
    isUsernameTakenByAnotherUser: (state) => (username, userId) =>
      Object.values(state.users).filter(
        (user) =>
          typeof user.properties[defaultUserProperties.USER_NAME]?.value === "string" &&
          typeof username === "string" &&
          user.properties[defaultUserProperties.USER_NAME]?.value.toLowerCase() === username.toLowerCase() &&
          user.id !== userId
      ).length > 0,

    /**
     * Indicates if a given email has already been associated with another loaded user.
     *
     * @returns {Boolean}
     */
    isEmailTakenByAnotherUser: (state) => (email, userId) =>
      Object.values(state.users).filter(
        (user) =>
          typeof user.properties[defaultUserProperties.EMAIL]?.value === "string" &&
          typeof email === "string" &&
          user.properties[defaultUserProperties.EMAIL]?.value.toLowerCase() === email.toLowerCase() &&
          user.id !== userId
      ).length > 0,

    /**
     * Get array of roles
     *
     * @returns {String[]}
     */
    roles: (state) => Object.values(state.roles),

    /**
     * Get the default role for new users
     *
     * @param state
     * @return {Object}
     */
    defaultRole: (state) => Object.values(state.roles).find((role) => role.name === defaultRoleName),

    /**
     * get sort options
     * @param state
     * @returns {*}
     */
    sortOptions: (state) => state.sortOptions.options,

    /**
     * get last sort setting
     * @param {*} state
     * @returns Number
     */
    lastActiveSort: (state) => state.sortOptions.active,

    /**
     * Returns a boolean indicating if another page of users can be retrieved
     * @returns Boolean
     */
    canRetrieveMoreUsers: () => userRequestHandler.CurrentPage < userRequestHandler.TotalPages - 1,
  },
}
